import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LOCAL_STORAGE_CART_TOKEN_KEY } from '@mkp/cart/models';
import { cartApiActions } from '@mkp/cart/actions';
import { AppConfig } from '@mkp/shared/util-app-config';
import { selectCartTokenValue } from './cart.selectors';
import { productSummaryPageActions } from '@mkp/product-summary/actions';
import { Account } from '@mkp/account/models';
import { selectSelectedAccount } from '@mkp/account/state';
import { SentryService } from '@mkp/core/util-sentry';
import { CartResource } from '@mkp/publication/data-access';
import { CartDto, toBillingAddress } from '@mkp/publication/models';
import { getMessageError, MessageError, ProductResource } from '@mkp/shared/data-access';
import { getFirstVariant, needsVacancyScope } from '@mkp/shared/util-product';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { User } from '@mkp/user/models';
import { selectLoggedInUser } from '@mkp/user/state-logged-in/selectors';
import { selectAccountFromVacancyRoute, selectVacancyIdFromRoute } from '@mkp/vacancy/state';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class CartEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private cartResource = inject(CartResource);
  private productResource = inject(ProductResource);
  private sentryService = inject(SentryService);
  private router = inject(Router);

  readonly createCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cartApiActions.createCart),
      concatLatestFrom(() => [
        this.store.select(selectAccountFromVacancyRoute),
        this.store.select(selectVacancyIdFromRoute),
      ]),
      filter(areAccountAndVacancyIdDefined),
      switchMap(([{ productCode, quantity = 1 }, { id: accountId }, vacancyId]) => {
        return this.productResource
          .get(productCode, accountId, needsVacancyScope(productCode) ? vacancyId : undefined)
          .pipe(
            switchMap((product) => {
              const variantCode = getFirstVariant(product).code;
              return this.cartResource.createCartWithProduct(accountId, variantCode, quantity).pipe(
                tap((res: CartDto) =>
                  localStorage.setItem(LOCAL_STORAGE_CART_TOKEN_KEY, res.tokenValue)
                ),
                map((cart) => cartApiActions.createCartSuccess({ cart, vacancyId })),
                catchError((error: unknown) =>
                  of(cartApiActions.createCartFailure({ error: getCartError(error) }))
                )
              );
            }),
            catchError((error: unknown) =>
              of(cartApiActions.createCartFailure({ error: getCartError(error) }))
            )
          );
      })
    )
  );

  readonly addCouponToCart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(productSummaryPageActions.applyCoupon),
        concatLatestFrom(() => [
          this.store.select(selectCartTokenValue).pipe(filter(Boolean)),
          this.store.select(selectSelectedAccount).pipe(filter(Boolean)),
          this.store.select(selectLoggedInUser).pipe(filter((user): user is User => !!user)),
        ]),
        switchMap(([{ coupon, walletId, overrideUser }, cartTokenValue, account, loggedInUser]) => {
          const billingUser = overrideUser ?? loggedInUser;
          const billingAddress = toBillingAddress(account, billingUser);
          return this.cartResource
            .upsertCouponInCart(cartTokenValue, coupon, billingAddress, billingUser.id, walletId)
            .pipe(
              map((cart) => cartApiActions.addCouponToCartSuccess({ cart })),
              catchError((error: unknown) =>
                of(cartApiActions.addCouponToCartFailure({ error: getCartError(error) }))
              )
            );
        })
      ),
    { useEffectsErrorHandler: false }
  );

  readonly removeCouponFromCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productSummaryPageActions.removeCoupon),
      concatLatestFrom(() => [
        this.store.select(selectCartTokenValue).pipe(filter(Boolean)),
        this.store.select(selectSelectedAccount).pipe(filter(Boolean)),
        this.store.select(selectLoggedInUser).pipe(filter((user): user is User => !!user)),
      ]),
      switchMap(([{ walletId, overrideUser }, cartTokenValue, account, loggedInUser]) => {
        const billingUser = overrideUser ?? loggedInUser;
        const billingAddress = toBillingAddress(account, billingUser);
        return this.cartResource
          .upsertCouponInCart(cartTokenValue, null, billingAddress, billingUser.id, walletId)
          .pipe(
            map((cart) => cartApiActions.removeCouponFromCartSuccess({ cart })),
            catchError((error: unknown) =>
              of(cartApiActions.removeCouponFromCartFailure({ error: getCartError(error) }))
            )
          );
      })
    )
  );

  readonly createCartFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(cartApiActions.createCartFailure),
        tap((error) => this.sentryService.captureWarning(error))
      );
    },
    { dispatch: false }
  );

  readonly fetchCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartApiActions.fetchCart),
      switchMap(({ tokenValue }) => {
        return this.cartResource.get(tokenValue).pipe(
          map((cart) => cartApiActions.fetchCartSuccess({ cart })),
          catchError((error: unknown) => {
            localStorage.removeItem(LOCAL_STORAGE_CART_TOKEN_KEY);
            this.router.navigate([AppConfig.routes.error404]);
            return of(cartApiActions.fetchCartFailure({ error: getCartError(error) }));
          })
        );
      })
    );
  });
}

const getCartError = (error: unknown): MessageError => getMessageError(error, 'CartEffects');

const areAccountAndVacancyIdDefined = <T>(
  inputArray: [T, Account | undefined, string | undefined]
): inputArray is [T, Account, string] => Boolean(inputArray[1]) && Boolean(inputArray[2]);
